import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Hero } from '../components/sales/Hero';
import { ProblemSection } from '../components/sales/ProblemSection';
import { MechanismSection } from '../components/sales/MechanismSection';
import { WhatYouGet } from '../components/sales/WhatYouGet';
import { OfferSection } from '../components/sales/OfferSection';
import { Guarantee } from '../components/sales/Guarantee';
import { FinalCTA } from '../components/sales/FinalCTA';
import { SocialProof } from '../components/sales/SocialProof';
import { Comparison } from '../components/sales/Comparison';
import { Objections } from '../components/sales/Objections';
import { BonusStack } from '../components/sales/BonusStack';
import { ForWho } from '../components/sales/ForWho';
import { UrgencyBar } from '../components/sales/UrgencyBar';
type ModalType = 'privacy' | 'terms' | null;
const modalContent: Record<'privacy' | 'terms', {
  title: string;
  content: string;
}> = {
  privacy: {
    title: 'Política de Privacidad',
    content: `Tu privacidad es importante para nosotros. Esta política explica cómo recopilamos y usamos tu información.

INFORMACIÓN QUE RECOPILAMOS
Al realizar una compra, recopilamos tu nombre, dirección de correo electrónico e información de pago procesada de forma segura por nuestro proveedor de pagos.

USO DE LA INFORMACIÓN
Usamos tu información únicamente para entregar el producto digital adquirido y enviarte comunicaciones relacionadas con tu compra.

COMPARTIR INFORMACIÓN
No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto cuando sea necesario para procesar tu pago o cumplir con la ley.

SEGURIDAD
Todos los pagos se procesan mediante conexiones SSL encriptadas. No almacenamos datos de tarjetas de crédito.

CONTACTO
Para cualquier consulta sobre privacidad, escríbenos a support@betterbysteps.com.`
  },
  terms: {
    title: 'Términos de Servicio',
    content: `Al adquirir Reset Metabólico 7 Días, aceptas los siguientes términos.

PRODUCTO DIGITAL
Este es un producto de descarga digital. Recibirás acceso inmediato por correo electrónico tras la compra. No se envían productos físicos.

GARANTÍA DE DEVOLUCIÓN
Ofrecemos una garantía de satisfacción de 7 días. Si no estás satisfecha con el producto, contáctanos a support@betterbysteps.com y te reembolsaremos el 100% del precio pagado.

USO PERSONAL
Este producto está licenciado para uso personal únicamente. No está permitida su redistribución, reventa o reproducción sin autorización escrita.

RESULTADOS
Los resultados descritos son individuales y pueden variar. Este producto no sustituye el consejo médico profesional.

CONTACTO
Para soporte, escríbenos a support@betterbysteps.com.`
  }
};
export function SalesPage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal]);
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-306">
      {/* Navigation / Header - Minimalist */}
      <nav className={`bg-white py-4 px-6 border-b border-gray-100 sticky top-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-md' : ''}`} data-id="element-307">
        <div className="max-w-7xl mx-auto flex justify-between items-center" data-id="element-308">
          <div className="font-serif text-xl font-bold text-brand-dark tracking-tight" data-id="element-309">
            RESET METABÓLICO
          </div>
          <div className="flex items-center gap-4" data-id="element-310">
            <AnimatePresence data-id="element-311">
              {scrolled && <motion.span initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 10
            }} className="text-sm font-bold text-brand-gold hidden sm:block" data-id="element-312">
                  $6.36 USD — Antes $148
                </motion.span>}
            </AnimatePresence>
            <a href="#offer" onClick={e => {
            e.preventDefault();
            document.getElementById('offer')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} className="text-sm font-bold text-brand-dark hover:text-brand-gold transition-colors" data-id="element-313">
              CONSEGUIR ACCESO
            </a>
          </div>
        </div>
      </nav>

      <main data-id="element-314">
        <Hero data-id="element-315" />
        <SocialProof data-id="element-316" />
        <ProblemSection data-id="element-317" />
        <ForWho data-id="element-318" />
        <MechanismSection data-id="element-319" />
        <Comparison data-id="element-320" />
        <WhatYouGet data-id="element-321" />
        <BonusStack data-id="element-322" />
        <OfferSection data-id="element-323" />
        <Guarantee data-id="element-324" />
        <Objections data-id="element-325" />
        <FinalCTA data-id="element-326" />
      </main>

      <UrgencyBar data-id="element-327" />

      <footer className="bg-brand-dark text-gray-400 py-12 border-t border-white/10 pb-24 md:pb-12" data-id="element-328">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm" data-id="element-329">
          <p className="mb-4" data-id="element-330">
            &copy; {new Date().getFullYear()} Reset Metabólico 7 Días. Todos los
            derechos reservados.
          </p>
          <div className="flex justify-center gap-6 mb-4 text-xs text-gray-500" data-id="element-331">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-gray-300 transition-colors underline underline-offset-2" data-id="element-332">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveModal('terms')} className="hover:text-gray-300 transition-colors underline underline-offset-2" data-id="element-333">
              Términos de Servicio
            </button>
            <a href="mailto:support@betterbysteps.com" className="hover:text-gray-300 transition-colors" data-id="element-334">
              Contacto
            </a>
          </div>
          <p className="max-w-2xl mx-auto text-xs text-gray-500" data-id="element-335">
            Descargo de responsabilidad: Este producto no sustituye el consejo
            médico profesional. Los resultados pueden variar. Consulte a su
            médico antes de comenzar cualquier programa de nutrición.
          </p>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-336">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-337">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-338">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-339">
                <h3 className="font-serif text-xl font-bold text-brand-dark" data-id="element-340">
                  {modalContent[activeModal].title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100" data-id="element-341">
                  <X className="h-5 w-5" data-id="element-342" />
                </button>
              </div>
              <div className="px-6 py-5 overflow-y-auto text-sm text-gray-600 leading-relaxed whitespace-pre-line" data-id="element-343">
                {modalContent[activeModal].content}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}